import { getDocs, collection, doc, updateDoc, getDoc, setDoc, deleteDoc, query, where, deleteField } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { auth } from '../../../firebase/config';
import { createUserWithEmailAndPassword, updateCurrentUser } from 'firebase/auth';

export type PSO = {
    id: string;
    name: string;
    contact?: string;
    email?: string;
    designation?: string;
    authUid?: string;
    isActive?: boolean;
    deactivationReason?: string;
    deactivationDate?: string;
    createdAt?: any;
    updatedAt?: any;
};

export async function fetchPSOs(bankId: string): Promise<PSO[]> {
    const psoSnap = await getDocs(collection(db, "banks", bankId, "pos"));
    const psoList: PSO[] = psoSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    } as PSO));

    // Sort by name
    psoList.sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        return nameA.localeCompare(nameB);
    });

    console.log('Fetched PSOs:', psoList.length);

    return psoList;
}

export async function updatePSO(bankId: string, psoId: string, data: Partial<PSO>): Promise<void> {
    const psoRef = doc(db, "banks", bankId, "pos", psoId);
    const updateData = { ...data, updatedAt: new Date() };
    await updateDoc(psoRef, updateData);
}

export async function registerPSOInAuth(bankId: string, psoId: string, email: string, password: string): Promise<string> {
    if (!bankId) throw new Error('Bank ID is required');
    if (!psoId) throw new Error('PSO ID is required');
    if (!email?.trim()) throw new Error('Email is required');
    if (!password || password.trim().length < 6) throw new Error('Password must be at least 6 characters');

    const currentUser = auth.currentUser;
    if (!currentUser?.uid) throw new Error('No user is currently logged in');

    const adminUid = currentUser.uid;
    const adminUser = currentUser;
    sessionStorage.setItem('creatingUser', adminUid);

    let authUid: string;
    try {
        const cred = await createUserWithEmailAndPassword(auth, email.trim().toLowerCase(), password.trim());
        authUid = cred.user.uid;

        try {
            await adminUser.reload();
            await updateCurrentUser(auth, adminUser);
        } catch {
            // keep UI stable via session flag
        }
    } catch (e: any) {
        sessionStorage.removeItem('creatingUser');
        const code = e?.code;
        if (code === 'auth/email-already-in-use') throw new Error('This email is already registered in the authentication system.');
        if (code === 'auth/invalid-email') throw new Error('Invalid email address.');
        if (code === 'auth/weak-password') throw new Error('Password is too weak (min 6 characters).');
        throw new Error(e?.message || 'Failed to create auth user');
    }

    // Keep Firestore doc id as-is (psoId), but store authUid so app can match login UID.
    const psoRef = doc(db, 'banks', bankId, 'pos', psoId);
    await updateDoc(psoRef, { authUid, updatedAt: new Date() });

    sessionStorage.removeItem('creatingUser');
    return authUid;
}

/** Deletes managedPsos docs then the pos document under banks/{bankId}/pos/{psoId}. */
export async function deletePSOStaffFirestoreRecord(bankId: string, psoId: string): Promise<void> {
    if (!bankId?.trim()) throw new Error('Bank ID is required');
    if (!psoId?.trim()) throw new Error('PSO document ID is required');

    const managedRef = collection(db, 'banks', bankId, 'pos', psoId, 'managedPsos');
    const managedSnap = await getDocs(managedRef);
    await Promise.all(managedSnap.docs.map((d) => deleteDoc(d.ref)));

    await deleteDoc(doc(db, 'banks', bankId, 'pos', psoId));
}

/**
 * Remove PSO assignment from all branches where branch.psoId matches this PSO.
 * We clear branch.psoId so the branch becomes unassigned.
 *
 * Some datasets store PSO assignment using either:
 * - The PSO document id (banks/{bankId}/pos/{psoDocId})
 * - A separate `psoId` field inside PSO docs (legacy/import)
 * So we attempt to match both when provided.
 */
export async function unassignPsoFromAllBranches(bankId: string, idsToMatch: string[]): Promise<number> {
    const ids = Array.from(new Set(idsToMatch.map((x) => (x || '').trim()).filter(Boolean)));
    if (!bankId?.trim()) throw new Error('Bank ID is required');
    if (ids.length === 0) return 0;

    const branchCol = collection(db, 'banks', bankId, 'branch');
    const branchDocIdsToClear = new Set<string>();

    for (const id of ids) {
        const snap = await getDocs(query(branchCol, where('psoId', '==', id)));
        snap.forEach((d) => branchDocIdsToClear.add(d.id));
    }

    await Promise.all(
        Array.from(branchDocIdsToClear).map((branchDocId) =>
            updateDoc(doc(db, 'banks', bankId, 'branch', branchDocId), { psoId: deleteField() } as any)
        )
    );

    return branchDocIdsToClear.size;
}

